import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../../shared/_TBComponents/Button';
import Modal from '../../../../shared/_TBComponents/Modal';
import Text from '../../../../shared/_TBComponents/Text';
import { showSuccessToast, showErrorToast } from '../../../../shared/utils/toastManager';
import { complexPasswordRegex } from '../../../../shared/utils/registrationSchemaHelper';

import AthleteProfileForm from './AthleteProfileForm';
import AthleteCalendarForm from './AthleteCalendarForm';
import AthleteProgramForm from './AthleteProgramForm';
import ContactNestedModal from './ContactNestedModal';
import {
  assignAthletePrograms,
  createUser,
} from '../../ducks/navigatorActions';

const FormWrapper = styled.div`
  width: min(850px, 100vw);
  min-width: min(600px, 100vw);
  min-height: min(800px, calc(100vh - 80px));
  max-height: calc(100vh - 150px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 8px;

  @media screen and (max-width: 767px) {
    width: 100vw;
    min-width: unset;
    min-height: calc(100vh - 80px);
    max-height: calc(100vh - 80px);
  }
`;

const FormLayout = styled(Form)`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding-bottom: 0 !important;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 8px 0 8px 20px;
`;

const TabRow = styled.div`
  display: flex;
  background: #F7F7F7;
  padding-top: 2px;
`;

const TabButton = styled.button`
  flex: 1;
  position: relative;
  border: none;
  background: ${(props) => (props.isActive ? '#FFF' : 'transparent')};
  padding: 8px 16px;
  cursor: pointer;

  @media (max-width: 767px) {
    padding: 8px 4px;
    font-size: 11px;
  }

  border-top: ${(props) => (props.isActive ? '2px solid #77A7FF' : 'none')};
  border-left: ${(props) => (props.isActive && (props.isLast || props.isMiddle) ? '2px solid #77A7FF' : 'none')};
  border-right: ${(props) => (props.isActive && (props.isFirst || props.isMiddle) ? '2px solid #77A7FF' : 'none')};
  border-bottom: ${(props) => (props.isActive ? 'none' : '2px solid #77A7FF')};
  border-top-left-radius: ${(props) => (props.isActive && (props.isLast || props.isMiddle) ? '12px' : '0')};
  border-top-right-radius: ${(props) => (props.isActive && (props.isFirst || props.isMiddle) ? '12px' : '0')};
  z-index: ${(props) => (props.isActive ? 2 : 1)};

  &:hover {
    background: #FFF;
    border-top-left-radius: ${(props) => (props.isActive && (props.isLast || props.isMiddle) ? '12px' : '0')};
    border-top-right-radius: ${(props) => (props.isActive && (props.isFirst || props.isMiddle) ? '12px' : '0')};
  }
`;

const TabContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  min-height: 0;
  overflow-y: auto;
  padding: 24px;

  @media (max-width: 767px) {
    padding: 16px;
  }
`;

const TabPanel = styled.div`
  display: ${(props) => (props.isActive ? 'block' : 'none')};
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  width: 100%;
  flex-shrink: 0;
  padding: 16px 24px;
  background: white;
  border-top: 1px solid #EEEEEE;
  box-sizing: border-box;

  @media (max-width: 767px) {
    flex-direction: column;
    gap: 6px;
    padding: 12px 16px;
  }
`;

const NestedModalContent = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 100%;
  gap: 10px;
`;

const NestedModalFooter = styled.div`
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const PROFILE_FIELDS = [
  'first', 'last', 'email', 'changePassword', 'confirmChangePassword', 'changePIN', 'confirmChangePIN',
];

const DEFAULT_PIC = process.env.ENVIRONMENT === 'PRODUCTION'
  ? 'https://s3.amazonaws.com/teambuildr-production-assets/pic/default.png'
  : 'https://s3.amazonaws.com/teambuildr-staging-assets/pic/default.png';

const AddAthleteModal = ({ isOpen, onClose, onCreated }) => {
  const dispatch = useDispatch();
  const formikDirtyRef = useRef(false);
  const tabContainerRef = useRef(null);

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const isCreatingUser = useSelector((state) => state.navigator.ui.isCreatingUser);
  const calendars = useSelector((state) => state.navigator.data.calendars);
  const groups = useSelector((state) => state.navigator.data.groups);
  const coachHasGroupRestrictions = !currentUser?.primaryAdmin && !currentUser?.fullUserAccess;
  const programs = useSelector((state) => state.navigator.data.programs);
  const usernamesEnabled = useSelector(
    (state) => state.auth.data.organizationSettings?.generalSettings?.usernamesEnabled,
  );

  const [activeTab, setActiveTab] = useState('profile');
  const [contacts, setContacts] = useState([]);
  const [selectedProgramIds, setSelectedProgramIds] = useState([]);
  const [nestedModalOpen, setNestedModalOpen] = useState(false);
  const [nestedModalType, setNestedModalType] = useState(null);
  const [editingContactIndex, setEditingContactIndex] = useState(null);
  const [contactForm, setContactForm] = useState({ name: '', email: '' });

  useEffect(() => {
    if (isOpen) {
      setActiveTab('profile');
      setContacts([]);
      setSelectedProgramIds([]);
      setNestedModalOpen(false);
      setNestedModalType(null);
      setContactForm({ name: '', email: '' });
      setEditingContactIndex(null);
    }
  }, [isOpen]);

  const openNestedModal = (type, contactIndex = null) => {
    setNestedModalType(type);
    if (type === 'editContact' && contactIndex !== null) {
      setEditingContactIndex(contactIndex);
      setContactForm(contacts[contactIndex]);
    } else if (type === 'deleteContact' && contactIndex !== null) {
      setEditingContactIndex(contactIndex);
    } else {
      setContactForm({ name: '', email: '' });
      setEditingContactIndex(null);
    }
    setNestedModalOpen(true);
  };

  const closeNestedModal = () => {
    setNestedModalOpen(false);
    setEditingContactIndex(null);
    setContactForm({ name: '', email: '' });
  };

  const handleCloseAttempt = () => {
    if (formikDirtyRef.current || contacts.length > 0 || selectedProgramIds.length > 0) {
      setNestedModalType('unsavedChanges');
      setNestedModalOpen(true);
    } else {
      onClose();
    }
  };

  const handleContactFormChange = (e) => {
    setContactForm({ ...contactForm, [e.target.name]: e.target.value });
  };

  const handleNestedModalSave = () => {
    if (nestedModalType === 'addContact') {
      setContacts([...contacts, { ...contactForm, id: Date.now() }]);
      closeNestedModal();
    } else if (nestedModalType === 'editContact') {
      const updated = [...contacts];
      updated[editingContactIndex] = contactForm;
      setContacts(updated);
      closeNestedModal();
    } else if (nestedModalType === 'deleteContact') {
      setContacts(contacts.filter((_, i) => i !== editingContactIndex));
      closeNestedModal();
    }
  };

  const SELECTBOX_STYLES = {
    control: (provided, state) => ({
      ...provided,
      fontFamily: 'Nunito Sans',
      fontSize: '14px',
      fontWeight: 600,
      borderColor: state.isFocused ? '#77A7FF' : '#D0D0D0',
      boxShadow: 'none',
      '&:hover': { borderColor: state.isFocused ? '#77A7FF' : '#D0D0D0' },
    }),
    option: (provided, state) => ({
      ...provided,
      fontFamily: 'Nunito Sans',
      fontSize: '14px',
      fontWeight: 600,
      backgroundColor: state.isFocused || state.isSelected ? 'rgba(119, 167, 255, 0.15)' : 'transparent',
      color: '#333333',
      '&:active': { backgroundColor: 'rgba(119, 167, 255, 0.25)' },
    }),
  };

  const CALENDAR_VISIBILITY_OPTIONS = [
    { id: '0', name: 'Show all future workouts' },
    { id: '1', name: 'Show only 1 week in advance' },
    { id: '2', name: 'Show only up to current day' },
  ];

  const validationSchema = Yup.object().shape({
    first: Yup.string().required('First name is required'),
    last: Yup.string().required('Last name is required'),
    email: usernamesEnabled
      ? Yup.string().trim().min(1, 'Username is required').required('Username is required')
      : Yup.string().email('Please enter a valid email address').required('Email address is required'),
    changePassword: usernamesEnabled
      ? Yup.string()
        .required('A password is required for username-based accounts')
        .matches(complexPasswordRegex, 'Passwords must contain 8 characters, 1 lowercase, 1 uppercase, 1 number, and 1 symbol.')
      : Yup.string()
        .test('password-validation', 'Passwords must contain 8 characters, 1 lowercase, 1 uppercase, 1 number, and 1 symbol.', (value) => {
          if (!value || value === '') return true;
          return complexPasswordRegex.test(value);
        }),
    confirmChangePassword: usernamesEnabled
      ? Yup.string()
        .required('Please confirm password')
        .oneOf([Yup.ref('changePassword')], 'Passwords do not match')
      : Yup.string().when('changePassword', {
        is: (val) => !!val && val.length > 0,
        then: (schema) => schema
          .required('Please confirm password')
          .oneOf([Yup.ref('changePassword')], 'Passwords do not match'),
        otherwise: (schema) => schema.oneOf([Yup.ref('changePassword'), ''], 'Passwords do not match'),
      }),
    changePIN: Yup.string()
      .test('pin-format', 'PIN must be 4 digits', (value) => {
        if (!value || value === '') return true;
        return /^\d{4}$/.test(value);
      }),
    confirmChangePIN: Yup.string().when('changePIN', {
      is: (val) => !!val && val.length > 0,
      then: (schema) => schema
        .required('Please confirm PIN')
        .oneOf([Yup.ref('changePIN')], 'PINs do not match'),
      otherwise: (schema) => schema.oneOf([Yup.ref('changePIN'), ''], 'PINs do not match'),
    }),
    groups: coachHasGroupRestrictions
      ? Yup.array().min(1, 'You must assign this athlete to at least one group.')
      : Yup.array(),
  });

  const initialValues = {
    first: '',
    last: '',
    email: '',
    ssoId: '',
    mobileCountry: '',
    mobileNumber: '',
    gender: null,
    externalId: '',
    changePassword: '',
    confirmChangePassword: '',
    changePIN: '',
    confirmChangePIN: '',
    smartabaseId: '',
    groups: coachHasGroupRestrictions && groups.length === 1
      ? [{ id: groups[0].id, name: groups[0].name }]
      : [],
    calendarAssignment: null,
    calendarVisibility: CALENDAR_VISIBILITY_OPTIONS[0],
    startDate: '',
    endDate: '',
    dateOffset: null,
  };

  const buildPayload = (values) => {
    if (usernamesEnabled) {
      const { email, ...rest } = values;
      return { ...rest, username: email };
    }
    return values;
  };

  const handleFormSubmit = (values, { setSubmitting }) => {
    dispatch(createUser(
      currentUser.accountCode,
      { ...buildPayload(values), contacts },
      {
        onSuccess: (newUser) => {
          const finish = () => {
            showSuccessToast(`${values.first} ${values.last} has been added.`);
            setSubmitting(false);
            if (onCreated) onCreated(newUser);
            onClose();
          };

          if (selectedProgramIds.length > 0 && newUser?.id) {
            dispatch(assignAthletePrograms(
              currentUser.accountCode,
              newUser.id,
              selectedProgramIds,
              {
                onSuccess: finish,
                onError: () => {
                  showSuccessToast(`${values.first} ${values.last} has been added.`);
                  showErrorToast('Athlete was created but program assignments failed. Please assign programs from the Edit Athlete modal.');
                  setSubmitting(false);
                  if (onCreated) onCreated(newUser);
                  onClose();
                },
              },
            ));
          } else {
            finish();
          }
        },
        onError: (err) => {
          showErrorToast(err?.response?.headers?.message || 'Unable to add athlete. Please try again.');
          setSubmitting(false);
        },
      },
    ));
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onRequestClose={handleCloseAttempt}
        customBorderRadius='8px'
        customPadding='0px'
        customMaxWidth='850px'
        customMinWidth='min(600px, 100vw)'
        customButton
        widthFitContent
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleFormSubmit}
          enableReinitialize
        >
          {(formikProps) => {
            const {
              isSubmitting, values, errors, touched, handleChange, handleBlur, setFieldValue,
              validateForm, setFieldTouched, dirty,
            } = formikProps;
            formikDirtyRef.current = dirty;

            const handleContinue = async (e) => {
              e.preventDefault();
              const formErrors = await validateForm();
              const hasProfileErrors = PROFILE_FIELDS.some((f) => formErrors[f]);
              if (hasProfileErrors) {
                PROFILE_FIELDS.forEach((f) => setFieldTouched(f, true, false));
                return;
              }
              setActiveTab(activeTab === 'profile' ? 'calendars' : 'programs');
            };

            return (
              <FormWrapper>
                <FormLayout autoComplete='off' data-1p-ignore='true' data-lpignore='true'>
                  <Header>
                    <Text styledTextProps={{ fontSize: '24px', fontWeight: 700 }}>
                      New Athlete
                    </Text>
                    <Button
                      icon='remove'
                      iconSize={15}
                      onClick={(e) => { e.preventDefault(); handleCloseAttempt(); }}
                      fontSize='13px'
                      txtColor='#9E9E9E'
                      noBorder
                    />
                  </Header>
                  <TabRow>
                    <TabButton
                      type='button'
                      isFirst
                      isActive={activeTab === 'profile'}
                      onClick={() => setActiveTab('profile')}
                    >
                      <Text>Profile Info</Text>
                    </TabButton>
                    <TabButton
                      type='button'
                      isMiddle
                      isActive={activeTab === 'calendars'}
                      onClick={() => setActiveTab('calendars')}
                    >
                      <Text>Calendar Assignments</Text>
                    </TabButton>
                    <TabButton
                      type='button'
                      isLast
                      isActive={activeTab === 'programs'}
                      onClick={() => setActiveTab('programs')}
                    >
                      <Text>Program Access</Text>
                    </TabButton>
                  </TabRow>

                  <TabContainer ref={tabContainerRef}>
                    <TabPanel isActive={activeTab === 'profile'}>
                      <AthleteProfileForm
                        athlete={{ pic: DEFAULT_PIC }}
                        contacts={contacts}
                        groups={groups}
                        selectBoxStyles={SELECTBOX_STYLES}
                        onOpenNestedModal={openNestedModal}
                        values={values}
                        errors={errors}
                        touched={touched}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        setFieldValue={setFieldValue}
                        isNew
                        usernamesEnabled={usernamesEnabled}
                        scrollContainerRef={tabContainerRef}
                      />
                    </TabPanel>
                    <TabPanel isActive={activeTab === 'calendars'}>
                      <AthleteCalendarForm
                        athleteCalendars={[]}
                        calendars={calendars}
                        calendarVisibilityOptions={CALENDAR_VISIBILITY_OPTIONS}
                        values={values}
                        setFieldValue={setFieldValue}
                        onOpenNestedModal={() => {}}
                        selectBoxStyles={SELECTBOX_STYLES}
                        isNew
                      />
                    </TabPanel>
                    <TabPanel isActive={activeTab === 'programs'}>
                      <AthleteProgramForm
                        athletePrograms={[]}
                        programs={programs}
                        onOpenNestedModal={() => {}}
                        addProgramMode
                        onSelectionChange={setSelectedProgramIds}
                      />
                    </TabPanel>
                  </TabContainer>

                  <ButtonContainer>
                    {activeTab === 'profile' && (
                    <>
                      <Button
                        type='button'
                        name='Continue to Calendar Assignments'
                        fontSize='14px'
                        txtColor='#444444'
                        bgColor='#FFF'
                        borderColor='#444444'
                        customBorderRadius='6px'
                        styledTextProps={{ fontWeight: 600 }}
                        onClick={handleContinue}
                      />
                      <Text styledTextProps={{ fontSize: '13px', color: '#9E9E9E' }}>or</Text>
                      <Button
                        name={isSubmitting || isCreatingUser ? 'Saving...' : 'Save and Finish'}
                        fontSize='14px'
                        txtColor='#FFF'
                        bgColor='#444444'
                        borderColor='#444444'
                        customBorderRadius='6px'
                        styledTextProps={{ fontWeight: 600 }}
                        disabled={isSubmitting || isCreatingUser}
                      />
                    </>
                    )}

                    {activeTab === 'calendars' && (
                    <>
                      <Button
                        type='button'
                        name='Continue to Program Assignments'
                        fontSize='14px'
                        txtColor='#444444'
                        bgColor='#FFF'
                        borderColor='#444444'
                        customBorderRadius='6px'
                        styledTextProps={{ fontWeight: 600 }}
                        onClick={handleContinue}
                      />
                      <Text styledTextProps={{ fontSize: '13px', color: '#9E9E9E' }}>or</Text>
                      <Button
                        name={isSubmitting || isCreatingUser ? 'Saving...' : 'Save and Finish'}
                        fontSize='14px'
                        txtColor='#FFF'
                        bgColor='#444444'
                        borderColor='#444444'
                        customBorderRadius='6px'
                        styledTextProps={{ fontWeight: 600 }}
                        disabled={isSubmitting || isCreatingUser}
                      />
                    </>
                    )}

                    {activeTab === 'programs' && (
                    <Button
                      name={isSubmitting || isCreatingUser ? 'Saving...' : 'Save and Finish'}
                      fontSize='14px'
                      txtColor='#FFF'
                      bgColor='#444444'
                      borderColor='#444444'
                      customBorderRadius='6px'
                      styledTextProps={{ fontWeight: 600 }}
                      disabled={isSubmitting || isCreatingUser}
                    />
                    )}
                  </ButtonContainer>
                </FormLayout>
              </FormWrapper>
            );
          }}
        </Formik>
      </Modal>

      <Modal
        isOpen={nestedModalOpen && nestedModalType === 'unsavedChanges'}
        onRequestClose={closeNestedModal}
        customBorderRadius='8px'
        customPadding='24px'
        customMaxWidth='400px'
        customMinHeight='200px'
        overlayBackgroundColor='rgba(0, 0, 0, 0.75)'
      >
        <NestedModalContent>
          <Text styledTextProps={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>
            Unsaved Changes
          </Text>
          <Text styledWrapperProps={{ marginBottom: '16px' }}>
            You have unsaved changes. Are you sure you want to leave?
          </Text>
          <NestedModalFooter>
            <Button
              name='Cancel'
              fontSize='14px'
              txtColor='#757575'
              bgColor='#FFF'
              borderColor='#EEEEEE'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              onClick={(e) => { e.preventDefault(); closeNestedModal(); }}
            />
            <Button
              name='Leave'
              fontSize='14px'
              txtColor='#FFF'
              bgColor='#FF0505'
              borderColor='#FF0505'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              onClick={(e) => { e.preventDefault(); closeNestedModal(); onClose(); }}
            />
          </NestedModalFooter>
        </NestedModalContent>
      </Modal>

      <ContactNestedModal
        isOpen={nestedModalOpen}
        onClose={closeNestedModal}
        type={nestedModalType}
        contactForm={contactForm}
        onContactFormChange={handleContactFormChange}
        onSave={handleNestedModalSave}
      />
    </>
  );
};

AddAthleteModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
};

AddAthleteModal.defaultProps = {
  onCreated: null,
};

export default AddAthleteModal;
